<template>
  <div class="dashboard-container">
    <!-- 顶部导航栏 -->
    <header class="header">
      <div class="header-content">
        <div class="left-section">
          <!-- 移动端菜单按钮 -->
          <button class="mobile-menu-btn" @click="toggleMobileMenu" v-show="isMobileView">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="3" y1="6" x2="21" y2="6"/>
              <line x1="3" y1="12" x2="21" y2="12"/>
              <line x1="3" y1="18" x2="21" y2="18"/>
            </svg>
          </button>
          
          <div class="logo-section">
            <div class="logo-icon">
              <img src="/huahu-icon.jpeg" alt="花狐" class="logo-image" />
            </div>
            <h1 class="app-title" @click="goToOfficial">{{ headerTitle }}</h1>
            
            <!-- APK版本描述横幅 -->
            <div v-if="showBanner && latestApkDescription" class="apk-notice-banner">
              <div class="banner-content">
                <span class="banner-icon">📢</span>
                <span class="banner-text">
                  <strong>{{ t('layout.versionNotice') }}</strong>{{ latestApkDescription }}
                </span>
                <button class="banner-close" @click="closeBanner" title="关闭">✕</button>
              </div>
            </div>
          </div>
        </div>
        <div class="user-section">
          <!-- 小喇叭图标（当横幅关闭时显示） -->
          <div v-if="!showBanner && latestApkDescription" class="apk-notice-icon" @click="openBanner" title="查看记录">
            <span class="icon-wrapper">
              <span class="icon-speaker">📢</span>
              <span v-if="!hasViewedBanner" class="red-dot"></span>
            </span>
            <span v-if="!hasViewedBanner" class="new-message-label">{{ t('layout.newUpdate') }}</span>
          </div>

          
          <div class="user-info">
            <div class="user-main-info">
              <span class="welcome-text">{{ userInfo.username || 'admin' }}</span>
              <div v-if="userInfo.username !== 'admin' && userInfo.expiry_date" class="expiry-info">
                <span 
                  :class="['expiry-badge', getExpiryStatus(userInfo.expiry_date)]"
                  :title="formatExpiryDateFull(userInfo.expiry_date)"
                >
                  {{ formatExpiryDate(userInfo.expiry_date) }}
                </span>
              </div>
            </div>
          </div>
          <button class="logout-btn" @click="handleLogout">{{ t('layout.logout') }}</button>
        </div>
      </div>
    </header>

    <!-- 版本历史时间线弹窗 -->
    <el-dialog
      v-model="showTimelineDialog"
      :title="t('layout.versionHistory')"
      width="600px"
      :close-on-click-modal="false"
      class="version-timeline-dialog"
    >
      <div v-loading="loadingHistory" class="timeline-container">
        <div v-if="versionHistory.length === 0 && !loadingHistory" class="empty-state">
          {{ t('layout.noVersionHistory') }}
        </div>
        <div v-else class="timeline-list">
          <div
            v-for="(item, index) in versionHistory"
            :key="index"
            class="timeline-item"
          >
            <div class="timeline-dot"></div>
            <div class="timeline-content">
              <div class="version-header">
                <span class="version-number">{{ t('layout.versionLabel', { version: item.version_code || item.version }) }}</span>
                <span v-if="item.upload_time" class="version-date">
                  {{ formatDate(item.upload_time) }}
                </span>
              </div>
              <div class="version-description" v-html="formatDescription(item.description)"></div>
            </div>
          </div>
        </div>
      </div>
      <template #footer>
        <el-button @click="showTimelineDialog = false">{{ t('common.close') }}</el-button>
      </template>
    </el-dialog>

    <!-- 主体布局 -->
    <div class="main-layout">
      <!-- 移动端菜单遮罩 -->
      <div v-if="showMobileMenu" class="mobile-menu-overlay" @click="closeMobileMenu"></div>
      
      <!-- 左侧导航栏 -->
      <aside class="sidebar" :class="{ 'mobile-open': showMobileMenu }">
        <nav class="nav-menu">
          <!-- 普通用户和配置管理员功能 -->
          <div v-if="userInfo.is_admin === 0 || userInfo.is_admin === 3"
               class="nav-item"
               :class="{ active: activeMenu === 'windows' }"
               @click="setActiveMenu('windows')">
            <span class="nav-text">{{ t('layout.windows') }}</span>
          </div>
          <div v-if="userInfo.is_admin === 0 || userInfo.is_admin === 3"
               class="nav-item"
               :class="{ active: activeMenu === 'scripts' }"
               @click="setActiveMenu('scripts')">
            <span class="nav-text">{{ t('layout.scripts') }}</span>
          </div>
          <div v-if="userInfo.is_admin === 0 || userInfo.is_admin === 3"
               class="nav-item"
               :class="{ active: activeMenu === 'data-stats' }"
               @click="setActiveMenu('data-stats')">
            <span class="nav-text">{{ t('layout.dataStats') }}</span>
          </div>
          <div
            v-if="(userInfo.is_admin === 0 || userInfo.is_admin === 3) && userGame !== GAME_KOREA"
            class="nav-item"
            :class="{ active: activeMenu === 'global-settings' }"
            @click="setActiveMenu('global-settings')"
          >
            <span class="nav-text">{{ t('layout.globalSettings') }}</span>
          </div>
          <div
            v-if="(userInfo.is_admin === 0 || userInfo.is_admin === 3) && userGame === GAME_KOREA"
            class="nav-item"
            :class="{ active: activeMenu === 'korea-global-settings' }"
            @click="setActiveMenu('korea-global-settings')"
          >
            <span class="nav-text">{{ t('layout.globalSettings') }}</span>
          </div>
          <!-- 管理员和代理共同功能 -->
          <div v-if="userInfo.is_admin === 1 || userInfo.is_admin === 2"
               class="nav-item"
               :class="{ active: activeMenu === 'users' }"
               @click="setActiveMenu('users')">
            <div class="nav-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                <circle cx="12" cy="7" r="4"/>
              </svg>
            </div>
            <span class="nav-text">用户管理</span>
          </div>
          <div v-if="userInfo.is_admin === 1 || userInfo.is_admin === 2"
               class="nav-item"
               :class="{ active: activeMenu === 'desktop-licenses' }"
               @click="setActiveMenu('desktop-licenses')">
            <span class="nav-text">桌面客户端卡密</span>
          </div>

          <!-- 超级管理员专用功能 -->
          <div v-if="canManageQuestionBank(userInfo)"
               class="nav-item"
               :class="{ active: activeMenu === 'question-bank' }"
               @click="setActiveMenu('question-bank')">
            <span class="nav-text">题库管理</span>
          </div>
          <div class="nav-item"
               :class="{ active: activeMenu === 'answer-records' }"
               @click="setActiveMenu('answer-records')">
            <span class="nav-text">答题记录</span>
          </div>
          <div v-if="userInfo.is_admin === 1"
               class="nav-item"
               :class="{ active: activeMenu === 'apk' }"
               @click="setActiveMenu('apk')">
            <div class="nav-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="4" y="4" width="16" height="16" rx="2" ry="2"/>
                <rect x="9" y="9" width="6" height="6"/>
                <line x1="9" y1="1" x2="9" y2="4"/>
                <line x1="15" y1="1" x2="15" y2="4"/>
              </svg>
            </div>
            <span class="nav-text">APK管理</span>
          </div>
          <div v-if="userInfo.is_admin === 1"
               class="nav-item"
               :class="{ active: activeMenu === 'statistics' }"
               @click="setActiveMenu('statistics')">
            <div class="nav-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M3 3v18h18"/>
                <path d="m19 9-5 5-4-4-3 3"/>
              </svg>
            </div>
            <span class="nav-text">活跃设备统计</span>
          </div>
        </nav>
      </aside>

      <!-- 主内容区域：子页面通过 .dashboard-route-view 参与 flex 高度链 -->
      <main class="main-content">
        <router-view v-slot="{ Component }">
          <component :is="Component" :class="routeViewClasses" />
        </router-view>
      </main>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import apk2API from '../api/apk2'
import { t } from '@/locales/zh'
import { GAME_KOREA } from '../constants/game'
import { canManageQuestionBank } from '../constants/questionBank'

export default {
  name: 'DashboardLayout',
  setup() {
    const router = useRouter()
    const route = useRoute()

    const routeViewClasses = computed(() => [
      'dashboard-route-view',
      route.meta.mobileLayout === 'fill' ? 'route-view-fill' : 'route-view-scroll'
    ])

    const userInfo = ref({
      username: '',
      is_admin: false
    })
    const userGame = computed(() => userInfo.value.game || '')
    const headerTitle = computed(() =>
      userGame.value === GAME_KOREA ? t('layout.appTitleKorea') : '花狐-冒险岛枫星'
    )
    const activeMenu = ref('')
    const showMobileMenu = ref(false)
    const isMobileView = ref(false)

    // APK描述相关状态
    const latestApkDescription = ref('')
    const showBanner = ref(false)
    const hasViewedBanner = ref(false) // 记录是否已经查看过横幅（点击过小喇叭打开过）
    const showTimelineDialog = ref(false) // 时间线弹窗显示状态
    const versionHistory = ref([]) // 版本历史列表
    const loadingHistory = ref(false) // 加载状态

    // 检测屏幕大小
    const checkMobileView = () => {
      isMobileView.value = window.innerWidth <= 640
      if (!isMobileView.value) {
        showMobileMenu.value = false
      }
    }

    onMounted(async () => {
      // 检查是否已登录
      const token = localStorage.getItem('token')
      const user = localStorage.getItem('user')
      
      if (!token || !user) {
        console.warn('请先登录')
        router.push('/login')
        return
      }
      
      // 初始化移动端视图检测
      checkMobileView()
      window.addEventListener('resize', checkMobileView)

      try {
        const parsedUser = JSON.parse(user)
        userInfo.value = {
          username: parsedUser.username || '',
          is_admin: parsedUser.is_admin || false,
          ...parsedUser
        }

        // 使用nextTick确保DOM已经渲染完成
        await nextTick()

        // 根据当前路由设置激活菜单
        const currentRoute = router.currentRoute.value.name
        if (currentRoute === 'WindowSettings') {
          activeMenu.value = 'windows'
        } else if (currentRoute === 'ScriptConfig') {
          activeMenu.value = 'scripts'
        } else if (currentRoute === 'DataStats') {
          activeMenu.value = 'data-stats'
        } else if (currentRoute === 'GlobalSettings') {
          activeMenu.value = 'global-settings'
        } else if (currentRoute === 'KoreaGlobalSettings') {
          activeMenu.value = 'korea-global-settings'
        } else if (currentRoute === 'UserManagement') {
          activeMenu.value = 'users'
        } else if (currentRoute === 'DesktopLicenseManagement') {
          activeMenu.value = 'desktop-licenses'
        } else if (currentRoute === 'QuestionBankManagement') {
          activeMenu.value = 'question-bank'
        } else if (currentRoute === 'AnswerRecordManagement') {
          activeMenu.value = 'answer-records'
        } else if (currentRoute === 'APKManagement') {
          activeMenu.value = 'apk'
        } else if (currentRoute === 'Statistics') {
          activeMenu.value = 'statistics'
        }

        // 加载最新APK描述（只对普通用户显示）
        if (userInfo.value.is_admin === 0 || userInfo.value.is_admin === 3) {
          loadLatestApkDescription()
        }

        // 从localStorage加载是否已查看过横幅
        const viewed = localStorage.getItem('hasViewedBanner')
        if (viewed === 'true') {
          hasViewedBanner.value = true
        }
      } catch (error) {
        console.error('用户信息解析失败:', error)
        localStorage.removeItem('token')
        localStorage.removeItem('user')
        router.push('/login')
      }
    })

    onUnmounted(() => {
      window.removeEventListener('resize', checkMobileView)
    })

    const setActiveMenu = (menu) => {
      activeMenu.value = menu
      // 关闭移动端菜单
      showMobileMenu.value = false
      
      if (menu === 'windows') {
        router.push('/dashboard/windows')
      } else if (menu === 'scripts') {
        router.push('/dashboard/scripts')
      } else if (menu === 'data-stats') {
        router.push('/dashboard/data-stats')
      } else if (menu === 'global-settings') {
        router.push('/dashboard/global-settings')
      } else if (menu === 'korea-global-settings') {
        router.push('/dashboard/korea-global-settings')
      } else if (menu === 'users') {
        router.push('/dashboard/users')
      } else if (menu === 'desktop-licenses') {
        router.push('/dashboard/users/desktop-licenses')
      } else if (menu === 'question-bank') {
        router.push('/dashboard/question-bank')
      } else if (menu === 'answer-records') {
        router.push('/dashboard/answer-records')
      } else if (menu === 'apk') {
        router.push('/dashboard/apk')
      } else if (menu === 'statistics') {
        router.push('/dashboard/statistics')
      }
    }

    // 移动端菜单控制
    const toggleMobileMenu = () => {
      showMobileMenu.value = !showMobileMenu.value
    }

    const closeMobileMenu = () => {
      showMobileMenu.value = false
    }

    const handleLogout = async () => {
      try {
        const confirmed = confirm('确定要退出登录吗？')
        if (!confirmed) return

        // 清除本地存储
        localStorage.removeItem('token')
        localStorage.removeItem('user')
        console.log('已退出登录')
        router.push('/login')
      } catch {
        // 用户取消操作
      }
    }

    // 跳转到官网
    const goToOfficial = () => {
      router.push('/official')
    }

    // 格式化到期时间
    const formatExpiryDate = (dateString) => {
      if (!dateString) return '未设置'
      try {
        const date = new Date(dateString)
        if (isNaN(date.getTime())) return '无效日期'
        const now = new Date()
        const isExpired = date < now
        const diffTime = Math.abs(date - now)
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
        
        if (isExpired) {
          return `已过期${diffDays}天`
        } else {
          if (diffDays === 0) {
            return '今日到期'
          } else if (diffDays === 1) {
            return '明日到期'
          } else {
            return `${diffDays}天后到期`
          }
        }
      } catch (error) {
        console.error('到期日期格式化失败:', error)
        return '格式错误'
      }
    }

    // 格式化完整的到期时间（用于悬停提示）
    const formatExpiryDateFull = (dateString) => {
      if (!dateString) return '未设置到期时间'
      try {
        const date = new Date(dateString)
        if (isNaN(date.getTime())) return '无效的到期时间'
        
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        const hours = String(date.getHours()).padStart(2, '0')
        const minutes = String(date.getMinutes()).padStart(2, '0')
        const seconds = String(date.getSeconds()).padStart(2, '0')
        
        const now = new Date()
        const isExpired = date < now
        const statusText = isExpired ? '已过期' : '到期时间'
        
        return `${statusText}: ${year}年${month}月${day}日 ${hours}:${minutes}:${seconds}`
      } catch (error) {
        console.error('完整到期日期格式化失败:', error)
        return '时间格式错误'
      }
    }

    // 获取到期状态
    const getExpiryStatus = (dateString) => {
      if (!dateString) return 'unknown'
      try {
        const date = new Date(dateString)
        if (isNaN(date.getTime())) return 'unknown'
        const now = new Date()
        const diffTime = date - now
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
        
        if (diffDays < 0) return 'expired'
        if (diffDays <= 3) return 'warning'
        if (diffDays <= 7) return 'soon'
        return 'normal'
      } catch (error) {
        return 'unknown'
      }
    }

    // 加载最新APK描述
    const loadLatestApkDescription = async () => {
      try {
        const response = await apk2API.getLatestAPK2('冒险岛枫星')
        const data = response?.data?.data || response?.data || {}
        
        // 获取所有版本的描述列表（不为空的）
        const allDescriptions = data.allDescriptions || []
        
        if (allDescriptions.length > 0) {
          // 将所有描述合并，用换行符分隔
          latestApkDescription.value = allDescriptions.join('\n\n')
          showBanner.value = true
        } else {
          // 如果没有allDescriptions，使用单个description作为兼容
          const description = data.description || (data.apk && data.apk.description)
          if (description && description.trim()) {
            latestApkDescription.value = description
            showBanner.value = true
          }
        }
      } catch (error) {
        // 静默失败，不影响主要功能
      }
    }

    // 关闭横幅的处理函数
    const closeBanner = () => {
      showBanner.value = false
      localStorage.setItem('hasViewedBanner', 'true')
    }

    // 打开时间线弹窗
    const openBanner = async () => {
      showTimelineDialog.value = true
      hasViewedBanner.value = true
      localStorage.setItem('hasViewedBanner', 'true')
      
      // 加载版本历史
      if (versionHistory.value.length === 0) {
        await loadVersionHistory()
      }
    }

    // 加载版本历史
    const loadVersionHistory = async () => {
      try {
        loadingHistory.value = true
        const response = await apk2API.getVersionHistory('冒险岛枫星')
        const data = response?.data?.data || response?.data || []
        versionHistory.value = Array.isArray(data) ? data : []
      } catch (error) {
        console.error('加载版本历史失败:', error)
        versionHistory.value = []
      } finally {
        loadingHistory.value = false
      }
    }

    // 格式化日期
    const formatDate = (dateString) => {
      if (!dateString) return ''
      try {
        const date = new Date(dateString)
        return date.toLocaleDateString('zh-CN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        })
      } catch (error) {
        return dateString
      }
    }

    // 格式化描述（将换行符转换为HTML）
    const formatDescription = (description) => {
      if (!description) return ''
      return description.replace(/\n/g, '<br>')
    }

    return {
      routeViewClasses,
      userInfo,
      userGame,
      GAME_KOREA,
      canManageQuestionBank,
      t,
      headerTitle,
      activeMenu,
      showMobileMenu,
      isMobileView,
      setActiveMenu,
      handleLogout,
      goToOfficial,
      formatExpiryDate,
      formatExpiryDateFull,
      getExpiryStatus,
      toggleMobileMenu,
      closeMobileMenu,
      latestApkDescription,
      showBanner,
      hasViewedBanner,
      closeBanner,
      openBanner,
      showTimelineDialog,
      versionHistory,
      loadingHistory,
      formatDate,
      formatDescription
    }
  }
}
</script>

<style scoped>
.dashboard-container {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f5f5f5;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.header {
  background: #fff;
  border-bottom: 1px solid #e5e5e5;
  position: sticky;
  top: 0;
  z-index: 100;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 24px;
  height: 60px;
  width: 100%;
}

.left-section {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.mobile-menu-btn {
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mobile-menu-btn:hover {
  background: rgba(0, 0, 0, 0.05);
  color: #333;
}

.mobile-menu-btn:active {
  background: rgba(0, 0, 0, 0.1);
}

.logo-section {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: nowrap;
  min-width: 0;
}

.logo-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.logo-image {
  width: 29px;
  height: 29px;
  object-fit: contain;
}

.app-title {
  color: #000;
  font-size: 20px;
  font-weight: 600;
  margin: 0;
  cursor: pointer;
  transition: color 0.2s ease;
  flex-shrink: 0;
  white-space: nowrap;
}

/* APK版本描述横幅样式 - 放在标题后面 */
.apk-notice-banner {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 4px 12px;
  border-radius: 6px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-left: 12px;
  margin-right: 18px;
  flex-shrink: 1;
  min-width: 0;
}

.banner-content {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-width: 0;
}

.banner-icon {
  font-size: 14px;
  flex-shrink: 0;
}

.banner-text {
  font-size: 12px;
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
}

.banner-text strong {
  font-weight: 600;
  margin-right: 4px;
}

.banner-close {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.2s;
  padding: 0;
  margin-left: 4px;
}

.banner-close:hover {
  background: rgba(255, 255, 255, 0.3);
}

/* 小喇叭图标样式 - 显示在用户名称前面 */
.apk-notice-icon {
  cursor: pointer;
  flex-shrink: 0;
  margin-right: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: transform 0.2s;
}

.apk-notice-icon:hover {
  transform: scale(1.05);
}

.icon-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon-speaker {
  font-size: 18px;
  display: block;
}

.red-dot {
  position: absolute;
  top: -2px;
  right: -2px;
  width: 8px;
  height: 8px;
  background: #ff3b30;
  border-radius: 50%;
  border: 2px solid white;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.new-message-label {
  font-size: 12px;
  color: #ff3b30;
  font-weight: 500;
  white-space: nowrap;
  background: rgba(255, 59, 48, 0.1);
  padding: 2px 6px;
  border-radius: 4px;
}

.app-title:hover {
  color: #1890ff;
}

.user-section {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
}

.user-info {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
}

.user-main-info {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
}

.welcome-text {
  color: #333;
  font-size: 14px;
  font-weight: 400;
}

.expiry-info {
  display: flex;
  align-items: center;
}

.expiry-badge {
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  cursor: help;
  transition: all 0.2s ease;
}

.expiry-badge:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.expiry-badge.normal {
  background: rgba(52, 199, 89, 0.1);
  color: #34c759;
}

.expiry-badge.soon {
  background: rgba(255, 149, 0, 0.1);
  color: #ff9500;
}

.expiry-badge.warning {
  background: rgba(255, 59, 48, 0.1);
  color: #ff3b30;
}

.expiry-badge.expired {
  background: rgba(255, 59, 48, 0.15);
  color: #ff3b30;
  font-weight: 600;
}

.expiry-badge.unknown {
  background: rgba(0, 0, 0, 0.05);
  color: #86868b;
}

.logout-btn {
  background: none;
  border: none;
  color: #1890ff;
  font-size: 14px;
  cursor: pointer;
  padding: 4px 8px;
  transition: color 0.2s ease;
}

.logout-btn:hover {
  color: #40a9ff;
}

.main-layout {
  flex: 1;
  display: flex;
  min-height: 0;
  overflow: hidden;
  position: relative;
}

/* 移动端菜单遮罩 */
.mobile-menu-overlay {
  position: fixed;
  top: 60px;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 49;
  animation: fadeInOverlay 0.3s ease;
}

@keyframes fadeInOverlay {
  from { opacity: 0; }
  to { opacity: 1; }
}

.sidebar {
  width: 150px;
  flex-shrink: 0;
  align-self: stretch;
  background: #fff;
  border-right: 1px solid #e5e5e5;
  padding: 16px 0;
  overflow-y: auto;
  overflow-x: hidden;
}

.nav-menu {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 0 16px;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #666;
  font-size: 14px;
  position: relative;
}

.nav-item:hover {
  background: #f0f0f0;
  color: #333;
}

.nav-item.active {
  background: #1890ff;
  color: white;
}

.nav-icon {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.nav-item.active .nav-icon {
  color: white;
}

.nav-item .nav-icon {
  color: #666;
}

.nav-text {
  font-size: 14px;
  white-space: nowrap;
}

.main-content {
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #f5f5f5;
  padding: 24px;
}

/* 子页面在此层滚动；使用 block 避免 flex 子项高度计算导致底部裁切 */
.dashboard-route-view {
  flex: 1;
  min-height: 0;
  min-width: 0;
  display: block;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  box-sizing: border-box;
  padding-bottom: 24px;
}

/* 响应式设计 */
@media (max-width: 1024px) {
  .user-info {
    gap: 10px;
  }
}

@media (max-width: 768px) {
  .dashboard-container {
    height: 100dvh;
    overflow: hidden;
  }
  
  .main-layout {
    flex: 1;
    min-height: 0;
    overflow: hidden;
    display: flex;
  }
  
  .main-content {
    flex: 1;
    min-height: 0;
    padding: 8px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  /* 窗口设置等：内部区域滚动，占满视口 */
  .dashboard-route-view.route-view-fill {
    flex: 1;
    min-height: 0;
    overflow: hidden;
    padding-bottom: 0;
  }

  /* 用户管理等：整页纵向滚动 */
  .dashboard-route-view.route-view-scroll {
    flex: 1;
    min-height: 0;
    overflow-x: hidden;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    padding-bottom: 16px;
  }
  
  .sidebar {
    width: 130px;
  }
  
  .header-content {
    padding: 0 16px;
  }
  
  .app-title {
    font-size: 18px;
  }
  
  .user-info {
    gap: 8px;
  }
}

@media (max-width: 640px) {
  .sidebar {
    position: fixed;
    left: -150px;
    top: 60px;
    height: calc(100vh - 60px);
    z-index: 50;
    transition: left 0.3s ease;
    box-shadow: 2px 0 8px rgba(0, 0, 0, 0.1);
    width: 150px;
  }
  
  /* 移动端菜单显示状态 */
  .sidebar.mobile-open {
    left: 0;
  }
  
  .main-content {
    margin-left: 0;
    width: 100%;
  }
  
  .nav-menu {
    padding: 0 12px;
  }
  
  .nav-item {
    padding: 10px 12px;
  }
  
  .header-content {
    padding: 0 12px;
  }
  
  .left-section {
    gap: 8px;
  }
  
  .logo-section {
    gap: 6px;
  }
  
  .app-title {
    font-size: 16px;
  }
  
  .user-section {
    gap: 8px;
  }
  
  .user-info {
    gap: 6px;
  }
  
  .user-main-info {
    flex-direction: column;
    align-items: flex-end;
    gap: 2px;
  }
  
  .welcome-text {
    font-size: 13px;
  }
  
  .expiry-info {
    justify-content: flex-end;
  }
  
  .expiry-badge {
    font-size: 10px;
    padding: 1px 4px;
  }
  
  .logout-btn {
    font-size: 13px;
  }

  /* 移动端适配 - 隐藏横幅和小喇叭图标 */
  .apk-notice-banner {
    display: none;
  }
  
  .apk-notice-icon {
    display: none;
  }
}

/* 版本历史时间线样式 */
.version-timeline-dialog .timeline-container {
  max-height: 500px;
  overflow-y: auto;
  padding: 20px 0;
}

.version-timeline-dialog .empty-state {
  text-align: center;
  color: #999;
  padding: 40px 0;
}

.version-timeline-dialog .timeline-list {
  position: relative;
  padding-left: 30px;
}

.version-timeline-dialog .timeline-item {
  position: relative;
  padding-bottom: 24px;
  padding-left: 20px;
  border-left: 2px solid #e5e5e5;
}

.version-timeline-dialog .timeline-item:last-child {
  border-left: none;
  padding-bottom: 0;
}

.version-timeline-dialog .timeline-dot {
  position: absolute;
  left: -6px;
  top: 4px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #1890ff;
  border: 2px solid white;
  box-shadow: 0 0 0 2px #1890ff;
}

.version-timeline-dialog .timeline-content .version-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}

.version-timeline-dialog .timeline-content .version-number {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.version-timeline-dialog .timeline-content .version-date {
  font-size: 12px;
  color: #999;
}

.version-timeline-dialog .timeline-content .version-description {
  font-size: 14px;
  color: #666;
  line-height: 1.6;
  white-space: pre-wrap;
}
</style>
